import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MODULES } from "@/data/course";
import { Check, TrendingUp, Sparkles, Lock, ArrowRight, ArrowLeft, AlertTriangle } from "lucide-react";

const FREE_FEATURES = [
  "Choose any 6 course modules",
  "All quizzes & XP / levels",
  "Interactive candlestick charts",
  "$100,000 paper trading simulator",
  "Risk calculator & glossary",
];
const PRO_FEATURES = [
  "All 15 course modules unlocked",
  "AI Trading Tutor (Claude)",
  "Video lessons with TJR",
  "Everything in Free",
  "Cancel anytime",
];

export default function ChoosePlan() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState("plans"); // plans | pick
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleModule = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 6 ? [...s, id] : s));
  };

  const confirmFree = async () => {
    if (selected.length !== 6) { toast.error("Please pick exactly 6 modules"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/plan/free", { modules: selected });
      setUser(data.user);
      toast.success("Free plan activated — enjoy!");
      navigate("/");
    } catch { toast.error("Could not activate plan"); }
    finally { setLoading(false); }
  };

  const startPro = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start checkout"); setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100 grid-bg">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-2.5 mb-10">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center glow-primary">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-extrabold text-lg">Trading Academy</span>
        </div>

        {step === "plans" && (
          <div className="animate-in">
            <div className="text-center mb-10">
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Choose your plan</h1>
              <p className="text-slate-400 mt-3">Welcome{user?.name ? `, ${user.name}` : ""}! Pick how you'd like to learn. You can upgrade anytime.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* Free */}
              <div className="bg-[#0F172A] border border-slate-800 rounded-3xl p-7 flex flex-col" data-testid="plan-free-card">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Free</div>
                <div className="flex items-baseline gap-1 mt-2">
                  <span className="font-display text-4xl font-black">€0</span>
                  <span className="text-slate-500 text-sm">/ forever</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {FREE_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <button data-testid="choose-free-btn" onClick={() => setStep("pick")}
                  className="mt-7 w-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white font-semibold rounded-xl py-3 transition-colors flex items-center justify-center gap-2">
                  Continue with Free <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {/* Pro */}
              <div className="relative bg-gradient-to-br from-blue-950/60 to-[#0F172A] border-2 border-blue-500/50 rounded-3xl p-7 flex flex-col glow-primary" data-testid="plan-pro-card">
                <div className="absolute -top-3 left-7 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Most Popular
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-blue-400">Pro</div>
                <div className="flex items-baseline gap-1 mt-2">
                  <span className="font-display text-4xl font-black">€3.99</span>
                  <span className="text-slate-400 text-sm">/ month</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {PRO_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-200">
                      <Check className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <button data-testid="choose-pro-btn" onClick={startPro} disabled={loading}
                  className="mt-7 w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold rounded-xl py-3 transition-colors flex items-center justify-center gap-2">
                  {loading ? "Redirecting…" : "Get Pro"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-center text-xs text-slate-600 mt-6 flex items-center justify-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Educational platform · virtual money only · test card 4242 4242 4242 4242
            </p>
          </div>
        )}

        {step === "pick" && (
          <div className="animate-in max-w-3xl mx-auto">
            <button onClick={() => setStep("plans")} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-4"><ArrowLeft className="w-4 h-4" /> Plans</button>
            <div className="mb-6">
              <h1 className="font-display text-3xl font-black tracking-tight">Pick your 6 free modules</h1>
              <p className="text-slate-400 mt-1">Selected <span className="font-mono text-blue-400 font-semibold">{selected.length}/6</span>. The rest unlock with Pro.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {MODULES.map((m) => {
                const on = selected.includes(m.id);
                const disabled = !on && selected.length >= 6;
                return (
                  <button key={m.id} data-testid={`pick-module-${m.id}`} onClick={() => toggleModule(m.id)} disabled={disabled}
                    className={`text-left rounded-xl border p-4 transition-all ${on ? "border-blue-500/60 bg-blue-500/10" : disabled ? "border-slate-800 bg-[#0F172A] opacity-40" : "border-slate-800 bg-[#0F172A] hover:border-blue-500/40"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-slate-500">Module {m.id}</span>
                      <span className={`w-5 h-5 rounded-md grid place-items-center ${on ? "bg-blue-500 text-white" : "bg-slate-800 text-transparent"}`}><Check className="w-3.5 h-3.5" /></span>
                    </div>
                    <div className="font-semibold text-sm mt-1">{m.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{m.stage}</div>
                  </button>
                );
              })}
            </div>
            <button data-testid="confirm-free-btn" onClick={confirmFree} disabled={loading || selected.length !== 6}
              className="mt-6 w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold rounded-xl py-3 transition-colors">
              {loading ? "Activating…" : "Start learning with these 6 modules"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
